import React, { useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { Plus, Filter as FilterIcon, RefreshCw, Save } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { OrderActionsProvider, useOrderActions } from '../../../contexts/OrderActionsContext';

import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu';
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay';
import { Filter, Header } from '../../../components/SubHeaderFilter/types';

import ConfirmationModal from '../../../components/ModalConfirmation';
import {
  OptionsButton,
  LeftSide,
  RightSide,
  SubHeaderContainer, RefreshIconContainer, FilterWrapper,
  TabsDiv,
  Tab
} from '../../../components/styles/subheader-style';
import { Content, SectionName } from './style';
import { Button } from './AddProduct/style';
import LoadMoreButton from '../../../components/LoadMoreButton';
import { LoadMoreProvider, useLoadMore } from '../../../contexts/LoadMoreContext';
import stock from '../../../assets/stock/TableMovementHeader/list-active.svg'
import resource from '../../../assets/home/resource.svg'
import steel from '../../../assets/home/steel.svg'

export interface Products {
  tabIndex: number
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
}


const productFinalTableHeaders: Header[] = [
  { key: 'name', label: 'Produto', type: 'string' },
  { key: 'norm', label: 'Norma', type: 'string' },
  { key: 'scriptName', label: 'Script', type: 'string' },
  { key: 'deadline', label: 'Prazo (dias)', type: 'number' },
  { key: 'cost', label: 'Custo (últ.)', type: 'currency' },
  { key: 'price', label: 'Preço (últ)', type: 'currency' },
];

const tabs = [
  { path: 'panel/stock', image: stock, name: 'Tecido' },
  { path: 'stock/resource', image: resource, name: 'Aviamentos' },
  { path: '', image: steel, name: 'Produto engenheirado' }
]

const ProductFinalsLayoutContent: React.FC<{ tabIndexActived: number }> = ({ tabIndexActived }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerSaveOrder } = useOrderActions();
  const queryClient = useQueryClient();
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = useLoadMore();
  const [tabIndex, setTabIndex] = useState<number>(0)
  const filterButtonRef = useRef<HTMLDivElement | null>(null);

  const [refresh, setRefresh] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const isCreatePage = location.pathname.includes('/panel/product-final/create');
  const isEditPage = location.pathname.includes('/panel/product-final/edit');
  const isFormPage = isCreatePage || isEditPage;
  const newProductId = location.state?.newProductId;
  const { search } = useOutletContext<{ search: string }>();

  const handleApplyFilter = (newFilter: Filter) => {
    setAppliedFilters(prev => [...prev, newFilter]);
  };
  const handleRemoveFilter = (index: number) => {
    setAppliedFilters(prev => prev.filter((_, i) => i !== index));
  };
  const handleClearAll = () => {
    setAppliedFilters([]);
  };
  const handleCancel = () => {
    setShowCancelModal(false);
    navigate('/panel/product-final');
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    setRefresh(prev => !prev);
  };

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>
            {isFormPage ? (isEditPage ? 'Editar Produto' : `Cadastrar produto engenheirado ${newProductId || ''}`) : 'Estoque'}
          </SectionName>
          {!isFormPage && (
            <TabsDiv>
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  $isFirst={index === 0}
                  $isLast={index === tabs.length - 1}
                  $isActive={index === tabIndexActived}
                  onClick={() => {
                    if (index !== tabIndexActived) {
                      if (index === 0) {
                        navigate('/panel/stock');
                      } else if (index === 1) {
                        navigate('/panel/resource');
                      } else if (index === 2) {
                        navigate('/panel/product-final');
                      }
                    }
                  }}
                >
                  <img src={tab.image} alt={`${tab.path}-icon`} />
                  {tab.name}
                </Tab>
              ))}
            </TabsDiv>
          )}
        </LeftSide>
        <RightSide>
          {!isFormPage && (
            <>
              <RefreshIconContainer onClick={handleRefresh} title="Atualizar">
                <RefreshCw size={20} />
              </RefreshIconContainer>
              <FilterWrapper>
                <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
                  <FilterIcon size={10} />
                </OptionsButton>
                {isFilterMenuOpen && (
                  <FilterMenu
                    headers={productFinalTableHeaders}
                    onApplyFilter={handleApplyFilter}
                    onClose={() => setIsFilterMenuOpen(false)}
                    anchorRef={filterButtonRef}
                    filters={appliedFilters}
                  />
                )}
              </FilterWrapper>
            </>
          )}

          {isFormPage ? (
            <>
              <Button variant='danger-outline' onClick={() => setShowCancelModal(true)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={triggerSaveOrder}>
                <Save size={18} style={{ marginRight: '8px' }} />
                {isEditPage ? 'Salvar Alterações' : 'Salvar Produto'}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => navigate('/panel/product-final/create')}>
              <Plus size={18} style={{ marginRight: '8px' }} />
              Cadastrar produto
            </Button>
          )}
        </RightSide>
      </SubHeaderContainer>

      <AppliedFiltersDisplay
        filters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAll}
      />

      <Content>
        <Outlet context={{ refresh, appliedFilters, tabIndex, setTabIndex, search }} />
      </Content>

      {showCancelModal && (
        <ConfirmationModal
          isOpen={showCancelModal}
          title={isEditPage ? "Cancelar Edição de Produto" : "Cancelar Criação de Produto"}
          message="Você tem certeza? Todas as alterações não salvas serão perdidas."
          actions={[
            { label: 'Confirmar', onClick: handleCancel, variant: 'danger' },
            { label: 'Voltar', onClick: () => setShowCancelModal(false), variant: 'outline' }
          ]}
        />
      )}
    </>
  );
};

const getActiveIndex = (pathname: string) => {
  if (pathname.includes('/panel/resource')) return 1;
  if (pathname.includes('/panel/product-final')) return 2;
  if (pathname.includes('/panel/stock')) return 0;
  return 0;
};

const ProductFinalsLayout: React.FC = () => {
  return (
    <LoadMoreProvider>
      <ProductFinalsLayoutContent tabIndexActived={getActiveIndex(location.pathname)} />
    </LoadMoreProvider>
  );
};

const ProductFinals: React.FC = () => {
  return (
    <OrderActionsProvider>
      <ProductFinalsLayout />
    </OrderActionsProvider>
  );
};

export default ProductFinals; 